import React, { useState, useEffect, useRef } from 'react';

/**
 * Props:
 *   onSubmit(code): Promise<void>
 *   onSwitchToBackup(): void  — passed by MfaChallenge if backup_codes is available
 *   submitting: boolean
 *   error: string
 *   backupAvailable: boolean
 */
export default function TotpChallengeWidget({
  onSubmit,
  onSwitchToBackup,
  submitting,
  error,
  backupAvailable,
  rememberDevice = false,
  onRememberDeviceChange,
  showRememberDevice = false,
  rememberDeviceDays = 30,
}) {
  const [code, setCode] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const clean = code.replace(/\s/g, '');
    if (clean.length === 6) onSubmit(clean);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-white mb-1">
          Authenticator code
        </label>
        <p className="text-xs text-gray-200 mb-2">
          Enter the 6-digit code from your authenticator app.
        </p>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9 ]*"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={8}
          className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-xl tracking-widest text-center bg-white"
          placeholder="••••••"
          disabled={submitting}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm" role="alert">
          {error}
        </div>
      )}

      {showRememberDevice && (
        <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => onRememberDeviceChange && onRememberDeviceChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            data-testid="remember-device-checkbox"
          />
          Remember this device for {rememberDeviceDays} days
        </label>
      )}

      <button
        type="submit"
        disabled={submitting || code.replace(/\s/g, '').length !== 6}
        className="w-full py-2 px-4 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? 'Verifying…' : 'Verify and sign in'}
      </button>

      {backupAvailable && (
        <button
          type="button"
          onClick={onSwitchToBackup}
          className="block w-full text-center text-sm text-gray-200 hover:text-white underline"
        >
          Use a backup code instead
        </button>
      )}
    </form>
  );
}
